/// <reference path="./globals.d.ts" />
import {
  tokenRefreshService,
  TokenRefreshHttpClient,
  TokenRefreshOptions,
} from './token-refresh-service';

export interface BootstrapServerTokenOptions extends TokenRefreshOptions {
  /** HTTP client used by the token refresh service for backend calls. */
  http: TokenRefreshHttpClient;
  /**
   * Default access-token lifetime (in seconds) when the backend doesn't inject
   * `window.tokenExpiresIn`. Defaults to 1 hour.
   */
  defaultExpiresIn?: number;
}

export interface BootstrapResult {
  /** Whether a server-injected token was found and the service initialized. */
  initialized: boolean;
}

/**
 * Initialize the unified token-refresh service from the backend-injected
 * globals (`window.oktaToken`, `window.refreshToken`, `window.tokenExpiresIn`).
 *
 * Apps call this once during startup (typically from `initialize.ts`) so that
 * any code consuming `getToken()` or `useTokenRefresh()` sees a valid token
 * straight away in production deployments.
 */
export function bootstrapServerToken(
  options: BootstrapServerTokenOptions,
): BootstrapResult {
  const { http, defaultExpiresIn = 3600, ...refreshOptions } = options;

  // Configure the singleton service so refresh calls know how to reach the
  // backend and how to surface errors.
  tokenRefreshService.configure(http, refreshOptions);

  // Make the service globally accessible for legacy non-React code paths.
  if (typeof window !== 'undefined') {
    window.tokenRefreshService = tokenRefreshService;
  }

  if (typeof window === 'undefined' || !window.oktaToken) {
    return { initialized: false };
  }

  tokenRefreshService.initializeTokens(
    window.oktaToken,
    window.refreshToken ?? '',
    window.tokenExpiresIn ?? defaultExpiresIn,
  );
  return { initialized: true };
}
